/**
 * Custom shapes (`shape: "path"`): a SwiftUI `Shape` drawn from its
 * `path(in:)`. The Swift side sends the path as SVG path data for the size the
 * element reported (`reportsSize`), so the element holds an `<svg>` whose
 * view box is that size, stretched to the element's box until the path for a
 * new size arrives (docs/ops-protocol.md, "Custom shapes"). A path for any
 * size (`affinePath`) is drawn at the size the element is laid out at, which
 * the renderer hands `layOutAffinePath` after each layout.
 *
 * An update whose shape's `animatableData` changed carries `frames`: the
 * paths at fractions of the change. When the commit animates the element,
 * the Animator calls `playPathFrames`, which draws the frame for the
 * animation's eased progress on each animation frame and ends on the final
 * path; otherwise the final path is drawn at once.
 */

import type { AffinePath, Animation, ShapeProps } from './protocol';
import { paintFor, type IdSource } from './chartDom';
import { easedProgress } from './animation';

const SVG_NS = 'http://www.w3.org/2000/svg';

/** The paths along an animated change (`index[i]` is the path at `progress[i]`). */
export interface PathFrames {
  progress: number[];
  index: number[];
  paths: string[];
}

interface PathState {
  svg: SVGSVGElement;
  path: SVGPathElement;
  final: string;
  frames: PathFrames | null;
  /** The running playback's frame request. */
  request: number | null;
  /** The props last drawn, and the size an `affinePath` was last laid out at (src/renderer.ts). */
  props: ShapeProps | null;
  laidOut: { width: number; height: number } | null;
}

const STATE = new WeakMap<HTMLElement, PathState>();
let gradientIds = 0;

/** Draws `props` (a `"path"` shape) into `el`. */
export function applyPathShape(el: HTMLElement, props: ShapeProps): void {
  const doc = el.ownerDocument;
  let state = STATE.get(el);
  if (!state || state.svg.parentElement !== el) {
    const svg = doc.createElementNS(SVG_NS, 'svg');
    svg.classList.add('sb-path');
    svg.setAttribute('aria-hidden', 'true');
    const path = doc.createElementNS(SVG_NS, 'path');
    svg.append(doc.createElementNS(SVG_NS, 'defs'), path);
    el.replaceChildren(svg);
    state = { svg, path, final: '', frames: null, request: null, props: null, laidOut: null };
    STATE.set(el, state);
  }
  stop(state);
  state.props = props;
  const { svg, path } = state;
  // A path for any size is drawn at the size the element was laid out at (none before its first layout).
  const affine = props.affinePath ?? null;
  const size = affine ? state.laidOut : props.size;
  const width = Number(size?.width) || 0;
  const height = Number(size?.height) || 0;
  if (width > 0 && height > 0) svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  else svg.removeAttribute('viewBox');
  svg.setAttribute('preserveAspectRatio', 'none');

  const defs = svg.querySelector('defs')!;
  defs.replaceChildren();
  const ids: IdSource = { next: 0, prefix: `sb-path${gradientIds++}` };
  const bbox = { x: 0, y: 0, width, height };
  const stroke = props.stroke;
  const fill = props.fill ? paintFor(doc, defs, props.fill, bbox, ids) : stroke ? 'none' : 'currentColor';
  path.style.fill = fill;
  path.style.fillRule = props.fillRule === 'evenodd' ? 'evenodd' : 'nonzero';
  if (stroke) {
    path.style.stroke = stroke.color ? paintFor(doc, defs, stroke.color, bbox, ids) : 'currentColor';
    path.style.strokeWidth = String(Number.isFinite(stroke.lineWidth) ? Math.max(0, stroke.lineWidth) : 1);
    path.style.strokeLinecap = stroke.lineCap ?? 'butt';
    path.style.strokeLinejoin = stroke.lineJoin ?? 'miter';
    path.style.strokeMiterlimit = String(stroke.miterLimit ?? 10);
    path.style.strokeDasharray = stroke.dash?.length ? stroke.dash.join(' ') : '';
    path.style.strokeDashoffset = stroke.dashPhase ? String(stroke.dashPhase) : '';
  } else {
    path.style.stroke = 'none';
  }
  // The stroke is not scaled with a stretched view box.
  path.setAttribute('vector-effect', 'non-scaling-stroke');

  state.final = affine ? (width > 0 && height > 0 ? affinePathData(affine, width, height) : '') : typeof props.path === 'string' ? props.path : '';
  state.frames = affine ? null : validFrames(props.frames);
  path.setAttribute('d', state.final);
}

/**
 * Draws `el`'s `affinePath` (if it has one) at the `width` by `height` it was
 * laid out at, when that is new: the renderer calls it after each layout.
 */
export function layOutAffinePath(el: HTMLElement, width: number, height: number): void {
  const state = STATE.get(el);
  if (!state?.props?.affinePath) return;
  if (state.laidOut && state.laidOut.width === width && state.laidOut.height === height) return;
  state.laidOut = { width, height };
  applyPathShape(el, state.props);
}

/** A number in SVG path data, as `Path` (@swiftbrowser/react) writes them. */
const NUMBER = /-?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?/g;

/** `affine`'s SVG path data for a `width` by `height` rectangle, its points rounded to hundredths as `Path` rounds them. */
export function affinePathData(affine: AffinePath, width: number, height: number): string {
  let i = 0;
  return affine.path.replace(NUMBER, (number) => {
    const value = Number(number) + width * (affine.width[i] ?? 0) + height * (affine.height[i] ?? 0);
    i += 1;
    const rounded = Math.round(value * 100) / 100;
    return Object.is(rounded, -0) ? '0' : String(rounded);
  });
}

/** Whether `el` is a path shape whose last update carried frames. */
export function hasPathFrames(el: HTMLElement): boolean {
  return STATE.get(el)?.frames != null;
}

/**
 * Plays `el`'s frames in `animation`'s timing: the Animator calls it for an
 * element whose update the commit animates. Returns whether there were frames.
 */
export function playPathFrames(el: HTMLElement, animation: Animation | undefined): boolean {
  const state = STATE.get(el);
  const frames = state?.frames;
  if (!state || !frames) return false;
  stop(state);
  const view = el.ownerDocument.defaultView;
  const now = () => view?.performance.now() ?? 0;
  const start = now();
  const draw = (): boolean => {
    const elapsed = now() - start;
    const done = isFinished(animation, elapsed);
    state.path.setAttribute('d', done ? state.final : frameAt(frames, easedProgress(animation, elapsed)));
    return done;
  };
  draw();
  const tick = () => {
    if (!el.isConnected || draw()) {
      state.request = null;
      state.path.setAttribute('d', state.final);
      return;
    }
    state.request = view?.requestAnimationFrame(tick) ?? null;
  };
  state.request = view?.requestAnimationFrame(tick) ?? null;
  return true;
}

/** The frame whose progress is nearest `progress`. */
export function frameAt(frames: PathFrames, progress: number): string {
  let best = 0;
  let distance = Infinity;
  for (let i = 0; i < frames.progress.length; i++) {
    const d = Math.abs(frames.progress[i]! - progress);
    if (d < distance) {
      distance = d;
      best = i;
    }
  }
  return frames.paths[frames.index[best] ?? 0] ?? '';
}

function isFinished(animation: Animation | undefined, elapsed: number): boolean {
  const delay = Math.max(0, (animation?.delay ?? 0) * 1000);
  const duration = Math.max(0, (animation?.duration ?? 0.35) * 1000);
  return elapsed >= delay + duration;
}

function stop(state: PathState): void {
  if (state.request != null) {
    state.svg.ownerDocument.defaultView?.cancelAnimationFrame(state.request);
    state.request = null;
  }
}

function validFrames(frames: ShapeProps['frames']): PathFrames | null {
  if (!frames || !Array.isArray(frames.progress) || !Array.isArray(frames.index) || !Array.isArray(frames.paths)) return null;
  if (frames.progress.length === 0 || frames.progress.length !== frames.index.length) return null;
  return frames;
}
